import React from 'react';
import { saveAs } from 'file-saver';
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer, Table, TableRow, TableCell, WidthType } from 'docx';
import { BusinessPlan, SECTION_METADATA } from '../../types/businessPlan';

interface BusinessPlanDOCXExportProps {
  plan: BusinessPlan;
}

const BusinessPlanDOCXExport: React.FC<BusinessPlanDOCXExportProps> = ({ plan }) => {
  // Function to convert markdown text to TextRun elements with formatting
  const parseMarkdownText = (text: string): TextRun[] => {
    let elements: TextRun[] = [];
    
    // First handle bold (**text** or __text__)
    const boldParts = text.split(/(\*\*.*?\*\*|__.*?__)/g);
    
    for (const part of boldParts) {
      if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
        // Add bold text
        elements.push(new TextRun({
          text: part.substring(2, part.length - 2),
          bold: true
        }));
      } else {
        // Handle italic (*text* or _text_) in the remaining text
        const italicParts = part.split(/(\*.*?\*|_.*?_)/g);
        
        for (const italicPart of italicParts) {
          if ((italicPart.startsWith('*') && italicPart.endsWith('*')) || (italicPart.startsWith('_') && italicPart.endsWith('_'))) {
            // Add italic text
            elements.push(new TextRun({
              text: italicPart.substring(1, italicPart.length - 1),
              italics: true
            }));
          } else if (italicPart.length > 0) {
            // Add regular text
            elements.push(new TextRun(italicPart));
          }
        }
      }
    }
    
    // If no formatting was found, return the text as a single TextRun
    if (elements.length === 0) {
      elements.push(new TextRun(text));
    }
    
    return elements;
  };

  // Function to convert markdown content to DOCX elements
  const convertMarkdownToDOCX = (markdown: string): (Paragraph | Table)[] => {
    const elements: (Paragraph | Table)[] = [];
    const lines = markdown.split('\n');
    
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      
      // Handle headers
      if (line.startsWith('### ')) {
        elements.push(new Paragraph({
          children: parseMarkdownText(line.substring(4)),
          heading: HeadingLevel.HEADING_3,
        }));
        i++;
      } else if (line.startsWith('## ')) {
        elements.push(new Paragraph({
          children: parseMarkdownText(line.substring(3)),
          heading: HeadingLevel.HEADING_2,
        }));
        i++;
      } else if (line.startsWith('# ')) {
        elements.push(new Paragraph({
          children: parseMarkdownText(line.substring(2)),
          heading: HeadingLevel.HEADING_1,
        }));
        i++;
      }
      // Handle tables
      else if (line.includes('|') && i + 1 < lines.length && lines[i + 1].includes('|')) {
        const tableLines = [];
        // Collect all table lines
        while (i < lines.length && lines[i].includes('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        
        // Parse table
        if (tableLines.length >= 2) {
          // Extract headers
          const headers = tableLines[0].split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
          
          // Create table with better formatting
          const tableRows = [
            new TableRow({
              children: headers.map(header => 
                new TableCell({
                  children: [
                    new Paragraph({
                      children: parseMarkdownText(header),
                      spacing: { after: 100 }
                    }),
                  ],
                  shading: {
                    fill: "E5E7EB" // Light gray background for headers
                  }
                })
              ),
            })
          ];
          
          // Add data rows (skip separator line if present)
          for (let j = 2; j < tableLines.length; j++) {
            const cells = tableLines[j].split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
            if (cells.length > 0) {
              tableRows.push(
                new TableRow({
                  children: cells.map(cell => 
                    new TableCell({
                      children: [new Paragraph({
                        children: parseMarkdownText(cell),
                        spacing: { after: 100 }
                      })],
                    })
                  ),
                })
              );
            }
          }
          
          elements.push(new Table({
            rows: tableRows,
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            columnWidths: headers.map(() => 100 / headers.length),
          }));
        }
      }
      // Handle bullet lists
      else if (line.startsWith('- ') || line.startsWith('* ')) {
        const listItems = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
          listItems.push(lines[i].substring(2));
          i++;
        }
        
        listItems.forEach(item => {
          elements.push(new Paragraph({
            children: parseMarkdownText(item),
            bullet: { level: 0 }
          }));
        });
      }
      // Handle numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const listItems = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          listItems.push(lines[i].replace(/^\d+\.\s/, ''));
          i++;
        }
        
        listItems.forEach((item, index) => {
          elements.push(new Paragraph({
            children: parseMarkdownText(item),
            numbering: { 
              reference: "numbering", 
              level: 0,
              instance: index + 1
            }
          }));
        });
      }
      // Handle regular paragraphs
      else if (line.trim() !== '') {
        elements.push(new Paragraph({
          children: parseMarkdownText(line),
        }));
        i++;
      }
      // Handle empty lines
      else {
        elements.push(new Paragraph({
          children: [new TextRun("")],
        }));
        i++;
      }
    }
    
    return elements;
  };

  const exportToDOCX = async () => {
    try {
      // Create document with basic styling
      const doc = new Document({
        numbering: {
          config: [
            {
              reference: "numbering",
              levels: [
                {
                  level: 0,
                  format: "decimal",
                  text: "%1.",
                  alignment: AlignmentType.START,
                },
              ],
            },
          ],
        },
        sections: [
          // Cover page
          {
            properties: {},
            children: [
              new Paragraph({
                text: plan.title || 'Business Plan',
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: "Business Plan",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: new Date().toLocaleDateString(),
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: "\n\n\n",
              }),
            ],
          },
          // Table of Contents
          {
            properties: {},
            children: [
              new Paragraph({
                text: "Table of Contents",
                heading: HeadingLevel.HEADING_1,
              }),
              ...Object.entries(SECTION_METADATA)
                .filter(([sectionId]) => sectionId !== 'cover-page' && sectionId !== 'table-of-contents')
                .map(([sectionId, metadata], index) => 
                  new Paragraph({
                    text: `${index + 1}. ${metadata.title}`,
                    indent: { left: 720 },
                  })
                ),
              new Paragraph({
                text: "\n\n\n",
              }),
            ],
          },
          // Sections
          ...Object.entries(plan.sections || {})
            .filter(([id]) => id !== 'cover-page' && id !== 'table-of-contents')
            .map(([id, section], index) => ({
              properties: {},
              children: [
                new Paragraph({
                  text: section.title,
                  heading: HeadingLevel.HEADING_1,
                }),
                // Convert markdown content to DOCX elements
                ...(section.content ? convertMarkdownToDOCX(section.content) : [new Paragraph("No content available for this section.")]),
                new Paragraph({
                  text: "\n\n",
                }),
              ],
            })),
        ],
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${plan.title || 'business-plan'}.docx`);
    } catch (error) {
      console.error('Error exporting to DOCX:', error);
      alert('Error exporting to DOCX. Please try again.');
    }
  };

  return (
    <button
      onClick={exportToDOCX}
      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
    >
      Export as DOCX
    </button>
  );
};

export default BusinessPlanDOCXExport;
